import { ArrowLeft, Check, Info, LoaderCircle, ShieldCheck, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Brand } from "../../components/Brand";
import { ApiError, api, getApiErrorMessage } from "../../lib/api";
import { authContinuationFromPath } from "../../lib/authContinuation";
import { talkBrand } from "../../lib/brand";
import { ui } from "../../lib/ui";
import { safeReturnPath } from "../../lib/navigation";
import type { RegistrationChallenge } from "../../lib/types";
import { useAuth } from "./AuthContext";
import { formatProofHashRate } from "./pow";

type Phase = "idle" | "requesting" | "solving" | "registering";
type WorkerMessage =
  | { type: "progress"; attempts: number; elapsedMs: number }
  | { type: "solved"; nonce: string; attempts: number; elapsedMs: number }
  | { type: "error"; message: string };

export function PowDisclosure() {
  const disclosureId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="pow-disclosure">
      <button
        type="button"
        aria-label="How the account-protection check works"
        aria-expanded={open}
        aria-controls={disclosureId}
        onClick={() => setOpen((current) => !current)}
      >
        <Info aria-hidden="true" />
        <span>How it works</span>
      </button>
      {open ? (
        <span id={disclosureId} className="pow-disclosure__popover" role="region" aria-label="How the account-protection check works">
          <strong>One local Argon2id proof</strong>
          <span>Each attempt runs in this browser. The 64 MiB memory cost makes bulk automated registration expensive.</span>
          <span>This is cancellable, one-time work—not cryptocurrency mining. Success is probabilistic, so {talkBrand.productName} does not show a misleading percentage or ETA.</span>
        </span>
      ) : null}
    </span>
  );
}

export function PowWorkStatus({ phase, attempts, elapsedMs, onCancel }: {
  phase: Exclude<Phase, "idle">;
  attempts: number;
  elapsedMs: number;
  onCancel: () => void;
}) {
  const phaseText = phase === "requesting"
    ? "Preparing one-time challenge"
    : phase === "registering"
      ? "Proof found · Creating your account"
      : "Running one-time proof";

  return (
    <div className="pow-status">
      <LoaderCircle className="spin" aria-hidden="true" size={22} />
      <div>
        <strong className="pow-status__phase" role="status" aria-live="polite" aria-atomic="true">{phaseText}</strong>
        {phase === "solving" ? (
          <dl className="pow-telemetry" aria-label="Account-protection work completed">
            <div><dt>Elapsed</dt><dd>{(elapsedMs / 1_000).toFixed(1)} s</dd></div>
            <div><dt>Hashes</dt><dd>{attempts.toLocaleString("en-US")}</dd></div>
            <div><dt>Rate</dt><dd>{formatProofHashRate(attempts, elapsedMs)}</dd></div>
          </dl>
        ) : <span>{phase === "requesting" ? `Requesting a fresh proof target from ${talkBrand.productName}…` : "Securing your password and opening the account…"}</span>}
      </div>
      {phase !== "registering" ? <button className="button button--small button--secondary pow-status__cancel" type="button" onClick={onCancel}>Cancel</button> : null}
    </div>
  );
}

export function RegisterPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [attempts, setAttempts] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const workerRef = useRef<Worker | null>(null);
  const challengeRef = useRef<RegistrationChallenge | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const requestedPath = safeReturnPath((location.state as { from?: unknown } | null)?.from);
  const continuation = authContinuationFromPath(requestedPath);
  const passwordChecks = useMemo(() => [{ id: "length", label: ui("12–1024 characters; long passphrases are welcome", "От 12 до 1024 символов. Длинная парольная фраза тоже подходит."), passes: password.length >= 12 && password.length <= 1024 }], [password]);
  const valid = /^[a-z0-9_]{3,32}$/.test(handle) && password.length >= 12 && password.length <= 1024 && password === confirmation;

  const cancelWork = useCallback((release = true) => {
    requestRef.current?.abort();
    requestRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;
    const challenge = challengeRef.current;
    challengeRef.current = null;
    if (release && challenge) void api.registration.cancel(challenge.id).catch(() => undefined);
    setPhase("idle");
    setAttempts(0);
    setElapsedMs(0);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
      workerRef.current?.terminate();
      const challenge = challengeRef.current;
      if (challenge) void api.registration.cancel(challenge.id).catch(() => undefined);
    };
  }, []);

  if (!auth.loading && auth.snapshot) return <Navigate replace to={requestedPath} />;

  async function begin() {
    if (!valid || phase !== "idle") return;
    if (talkBrand.id === "chess") {
      setError("");
      setFieldErrors({});
      setPhase("registering");
      try {
        const snapshot = await api.registration.registerDirect({ handle, password, displayName: handle });
        if (!snapshot?.user?.id || !snapshot.csrfToken) throw new Error(ui("The server returned an incomplete account session.", "Сервер вернул неполную сессию. Попробуйте ещё раз."));
        auth.setSnapshot(snapshot);
        navigate(requestedPath, { replace: true });
      } catch (nextError) {
        if (nextError instanceof ApiError) setFieldErrors(nextError.fields ?? {});
        setError(getApiErrorMessage(nextError));
        setPhase("idle");
      }
      return;
    }
    if (typeof Worker === "undefined" || typeof WebAssembly === "undefined") {
      setError("This browser cannot run the account-protection check. Use a current version of Chrome, Firefox, or Safari.");
      return;
    }
    setError("");
    setFieldErrors({});
    setPhase("requesting");
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const challenge = await api.registration.challenge(handle, controller.signal);
      requestRef.current = null;
      if (!mountedRef.current || controller.signal.aborted) {
        void api.registration.cancel(challenge.id).catch(() => undefined);
        return;
      }
      challengeRef.current = challenge;
      const worker = new Worker(new URL("./pow.worker.ts", import.meta.url), { type: "module" });
      workerRef.current = worker;
      setPhase("solving");
      worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
        const message = event.data;
        if (message.type === "progress") {
          setAttempts(message.attempts);
          setElapsedMs(message.elapsedMs);
          return;
        }
        if (message.type === "error") {
          setError(message.message);
          cancelWork();
          return;
        }
        worker.terminate();
        workerRef.current = null;
        setAttempts(message.attempts);
        setElapsedMs(message.elapsedMs);
        setPhase("registering");
        void api.registration.register({ handle, password, challengeId: challenge.id, nonce: message.nonce })
          .then((snapshot) => {
            challengeRef.current = null;
            if (!snapshot?.user?.id || !snapshot.csrfToken) throw new Error(ui("The server returned an incomplete account session.", "Сервер вернул неполную сессию. Попробуйте ещё раз."));
            auth.setSnapshot(snapshot);
            navigate(requestedPath, { replace: true });
          })
          .catch((nextError: unknown) => {
            challengeRef.current = null;
            if (nextError instanceof ApiError) setFieldErrors(nextError.fields ?? {});
            setError(getApiErrorMessage(nextError));
            setPhase("idle");
          });
      });
      worker.addEventListener("error", () => {
        setError("The browser check stopped unexpectedly. Try again.");
        cancelWork();
      });
      worker.postMessage({ type: "solve", challenge, handle });
    } catch (nextError) {
      requestRef.current = null;
      if (!mountedRef.current || controller.signal.aborted) return;
      if (nextError instanceof ApiError) setFieldErrors(nextError.fields ?? {});
      setError(getApiErrorMessage(nextError));
      cancelWork();
    }
  }

  const busy = phase !== "idle";
  return (
    <main id="main-content" className="auth-layout" tabIndex={-1}>
      <section className="auth-context auth-context--register">
        <Brand />
        <div><h1>{talkBrand.id === "chess" ? "Аккаунт для комнат" : "One account. Your rooms."}</h1><p>{talkBrand.id === "chess" ? "Этим аккаунтом создают комнату партии. Соперник может зайти по ссылке без регистрации." : "No email required. A memory-hard browser check makes bulk automated sign-ups expensive without handing your data to a CAPTCHA provider."}</p></div>
        <Link className="quiet-link" to="/"><ArrowLeft aria-hidden="true" size={16} />{ui("Back home", "На главную")}</Link>
      </section>
      <section className="auth-surface" aria-labelledby="register-title">
        <div className="auth-surface__heading"><ShieldCheck aria-hidden="true" size={24} /><div><h2 id="register-title">{continuation ? ui("Create an account and continue", "Создать аккаунт и продолжить") : ui("Create an account", "Создать аккаунт")}</h2><p>{continuation ? ui(`Your selected ${talkBrand.productName} task will still be waiting.`, "Выбранное действие никуда не денется.") : ui("Choose the identity you will use in rooms.", "Это имя будет видно в комнатах.")}</p></div></div>
        <form className="form-stack" onSubmit={(event) => { event.preventDefault(); void begin(); }}>
          <label className="field"><span>{ui("Handle", "Имя аккаунта")}</span><span className="input-prefix"><span aria-hidden="true">@</span><input autoFocus required minLength={3} maxLength={32} pattern="[a-z0-9_]+" autoComplete="username" disabled={busy} aria-invalid={Boolean(fieldErrors.handle)} value={handle} onChange={(event) => setHandle(event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} /></span>{fieldErrors.handle ? <small className="field-error">{fieldErrors.handle}</small> : <small>{ui("3–32 lowercase letters, numbers, or underscores.", "3–32 символа: строчные латинские буквы, цифры или подчёркивание.")}</small>}</label>
          <label className="field"><span>{ui("Password", "Пароль")}</span><input type="password" required minLength={12} maxLength={1024} autoComplete="new-password" disabled={busy} aria-invalid={Boolean(fieldErrors.password)} value={password} onChange={(event) => setPassword(event.target.value)} />{fieldErrors.password ? <small className="field-error">{fieldErrors.password}</small> : null}</label>
          <ul className="password-checks" aria-label={ui("Password requirements", "Требования к паролю")}>{passwordChecks.map((check) => <li key={check.id} className={check.passes ? "passes" : undefined}>{check.passes ? <Check aria-hidden="true" size={14} /> : <X aria-hidden="true" size={14} />}{check.label}</li>)}</ul>
          <label className="field"><span>{ui("Confirm password", "Пароль ещё раз")}</span><input type="password" required minLength={12} maxLength={1024} autoComplete="new-password" disabled={busy} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />{confirmation && confirmation !== password ? <small className="field-error">{ui("Passwords do not match.", "Пароли не совпадают.")}</small> : null}</label>

          {talkBrand.id === "chess" ? null : phase !== "idle" ? (
            <PowWorkStatus phase={phase} attempts={attempts} elapsedMs={elapsedMs} onCancel={() => cancelWork()} />
          ) : <div className="pow-explainer"><ShieldCheck aria-hidden="true" size={18} /><span><strong>Account-protection check</strong> Starts only when you submit and uses 64 MiB of memory. It usually takes a few seconds.</span><PowDisclosure /></div>}

          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button button--primary button--wide" type="submit" disabled={busy || !valid}>{talkBrand.id === "chess" ? (busy ? "Создаём аккаунт…" : "Создать аккаунт") : phase === "requesting" ? "Preparing check…" : busy ? "Check running…" : continuation ? "Check, create, and continue" : "Check and create account"}</button>
        </form>
        <div className="auth-footnote"><span>{ui("Already have an account?", "Уже есть аккаунт?")}</span><Link to="/login" state={{ from: requestedPath }}>{ui("Sign in", "Войти")}</Link></div>
      </section>
    </main>
  );
}
